import type { PostgrestClientOptions, PostgrestFilterBuilder } from "@supabase/postgrest-js"

import { FilterState } from "@/types/application"
import { DEFAULT_APPLICATION_FILTERS } from "@/utils/applicationFilterState"

import type { Database, Tables } from "../../database.types"

type ApplicationsQuery<
  ClientOptions extends PostgrestClientOptions,
  Result,
  RelationName,
  Relationships,
  Method,
> = PostgrestFilterBuilder<
  ClientOptions,
  Database["public"],
  Tables<"applications">,
  Result,
  RelationName,
  Relationships,
  Method
>

const BOOKING_FILTERS = [
  { key: "orientationBooked", alias: "orientation_filter", sessionType: "orientation" },
  { key: "vehicleBooked", alias: "vehicle_filter", sessionType: "vehicle_collection" },
] as const

type ApplicationSearch = { kind: "applicationId" | "personId" | "applicant"; term: string }

const parseSearch = (searchQuery: string): ApplicationSearch | null => {
  const term = searchQuery.replace(/[,()]/g, " ").trim().toLowerCase()
  if (!term) return null
  if (term.startsWith("app-")) return { kind: "applicationId", term }
  if (/^\d+$/.test(term)) return { kind: "personId", term }
  return { kind: "applicant", term }
}

const whereExists = <
  ClientOptions extends PostgrestClientOptions,
  Result,
  RelationName,
  Relationships,
  Method,
>(
  query: ApplicationsQuery<ClientOptions, Result, RelationName, Relationships, Method>,
  alias: string,
  exists: boolean,
) => (exists ? query.not(alias, "is", null) : query.is(alias, null))

const hasLocation = (filters: Partial<FilterState>) =>
  !!filters.location && filters.location !== DEFAULT_APPLICATION_FILTERS.location

export function applicationFilterEmbeds(
  filters: Partial<FilterState>,
  searchQuery: string = "",
): string {
  let embeds = ""
  if (hasLocation(filters)) {
    embeds += "location_filter:job_id!inner(depot_id),"
  }
  const search = parseSearch(searchQuery)
  if (search?.kind === "applicant") {
    embeds += "applicant_search:applicant_id!inner(first_name, last_name, email),"
  }
  if (search?.kind === "personId") {
    embeds += "person_search:people!applications_person_id_fkey!inner(person_id),"
  }
  for (const { key, alias } of BOOKING_FILTERS) {
    if (filters[key] != null) {
      embeds += `${alias}:session_bookings(id, session:session_id!inner(type)),`
    }
  }
  if (filters.trialBooked != null) {
    embeds += "trial_filter:people!applications_person_id_fkey(id, shifts!inner(id)),"
  }
  if (filters.personStatus) {
    embeds += "person_filter:people!applications_person_id_fkey!inner(status),"
  }
  return embeds
}

export function applyApplicationFilters<
  ClientOptions extends PostgrestClientOptions,
  Result,
  RelationName,
  Relationships,
  Method,
>(
  query: ApplicationsQuery<ClientOptions, Result, RelationName, Relationships, Method>,
  filters: Partial<FilterState>,
  searchQuery: string = "",
) {
  if (hasLocation(filters)) {
    query = query.eq("location_filter.depot_id", filters.location)
  }
  if (filters.jobPostings?.length) {
    query = query.in("job_id", filters.jobPostings)
  }
  if (filters.stage) {
    query = query.eq("current_stage", filters.stage)
  }
  if (filters.stageReason) {
    query = query.eq("stage_reason", filters.stageReason)
  }
  for (const { key, alias, sessionType } of BOOKING_FILTERS) {
    const booked = filters[key]
    if (booked == null) continue
    query = query.eq(`${alias}.session.type`, sessionType).is(`${alias}.cancelled_at`, null)
    query = whereExists(query, alias, booked)
  }
  if (filters.trialBooked != null) {
    query = query.eq("trial_filter.shifts.type", "Trial").eq("trial_filter.shifts.status", "Filled")
    query = whereExists(query, "trial_filter", filters.trialBooked)
  }
  if (filters.personStatus) {
    query = query.eq("person_filter.status", filters.personStatus)
  }
  const search = parseSearch(searchQuery)
  if (search?.kind === "applicationId") {
    query = query.ilike("application_id", `*${search.term}*`)
  } else if (search?.kind === "personId") {
    query = query.eq("person_search.person_id", search.term)
  } else if (search?.kind === "applicant") {
    const like = `*${search.term}*`
    query = query.or(`first_name.ilike.${like},last_name.ilike.${like},email.ilike.${like}`, {
      referencedTable: "applicant_search",
    })
  }
  return query
}
